// NPM Imports
import { Modifier, EditorState } from "draft-js";

// Internal Imports
import { setBlockData } from "../../../utils/draftUtils";
import { DOCUMENTS } from "../../../constants";

// Restructuring
const { EDITOR_CUSTOM_STYLES_MAP } = DOCUMENTS;

const getCurrentlySelectedBlock = editorState => {
  const selection = editorState.getSelection();
  const startKey = selection.getStartKey();
  let endKey = selection.getEndKey();
  const content = editorState.getCurrentContent();
  let target = selection;

  // Triple-click can lead to a selection that includes offset 0 of the
  // following block. The `SelectionState` for this case is accurate, but
  // we should avoid toggling block type for the trailing block because it
  // is a confusing interaction.
  if (startKey !== endKey && selection.getEndOffset() === 0) {
    const blockBefore = content.getBlockBefore(endKey);
    if (!blockBefore) {
      throw new Error("Got unexpected null or undefined");
    }

    endKey = blockBefore.getKey();
    target = target.merge({
      anchorKey: startKey,
      anchorOffset: selection.getStartOffset(),
      focusKey: endKey,
      focusOffset: blockBefore.getLength(),
      isBackward: false,
    });
  }

  const hasAtomicBlock = content
    .getBlockMap()
    .skipWhile((_, k) => k !== startKey)
    .takeWhile((_, k) => k !== endKey)
    .some(v => v.getType() === "atomic");

  const currentBlock = content.getBlockForKey(startKey);

  return {
    content,
    currentBlock,
    hasAtomicBlock,
    target,
  };
};

const getStylesFromMetaData = (metaData, editorState) => {
  const styles = [];
  // Getting the inline styles of last char of last block
  const oldContentState = editorState.getCurrentContent();
  const oldLastBlock = oldContentState.getLastBlock();
  const oldBlockTextLength = oldLastBlock.getLength();
  const oldBlockLastCharStyles = oldLastBlock
    .getInlineStyleAt(oldBlockTextLength - 1)
    .toArray();

  // For old style format, need to get the all styles in a single array
  // (because, some old styles were coming inside an array not a string)
  let styleList = [];
  oldBlockLastCharStyles.forEach(style => {
    if (typeof style === "string") {
      styleList.push(style);
    } else if (Array.isArray(style)) {
      styleList = [...styleList, ...style];
    }
  });

  if (styleList && styleList.length > 0) {
    styleList.forEach(style => {
      if (style.startsWith("FONTSIZE_")) {
        styles.push(`font-size: ${EDITOR_CUSTOM_STYLES_MAP[style].fontSize}`);
      } else if (style.startsWith("FONTCOLOR_")) {
        const getColor = style.split("_")[1];
        if (getColor) {
          styles.push(`color: #${getColor} `);
        }
      } else if (style.startsWith("FONTFAMILY_")) {
        const getColor = style.split("_")[1];
        if (getColor) {
          styles.push(
            `font-family: ${EDITOR_CUSTOM_STYLES_MAP[style].fontFamily}`,
          );
        }
      }
    });
  }

  // If there are no styles found on last char of last block(empty editor case), then
  // getting the styles from meta data
  if (styles.length === 0 && metaData) {
    if (metaData.FONTCOLOR) {
      const getColor = metaData.FONTCOLOR.split("_")[1];
      if (getColor) {
        styles.push(`color: #${getColor} `);
      }
    }
    if (metaData.FONTSIZE) {
      styles.push(
        `font-size: ${EDITOR_CUSTOM_STYLES_MAP[metaData.FONTSIZE].fontSize}`,
      );
    }
    if (metaData.FONTFAMILY) {
      styles.push(
        `font-family: ${
          EDITOR_CUSTOM_STYLES_MAP[metaData.FONTFAMILY].fontFamily
        }`,
      );
    }
  }

  return styles;
};

const splitBlock = (editorState, metaData) => {
  // Original split logic
  const contentState = Modifier.splitBlock(
    editorState.getCurrentContent(),
    editorState.getSelection(),
  );

  const splitState = EditorState.push(editorState, contentState, "split-block");

  // Assign alignment if previous block has alignment.
  // Note that `currentBlock` is the block that was selected before the split.
  const { currentBlock } = getCurrentlySelectedBlock(editorState);

  // get existing styles
  const blockData = currentBlock.data;
  const elementStyle = blockData.get("elementStyle");

  // Note :: there are two cases for alignment possible ,
  // 1'st is "text-align" and 2'nd is "justify-content" case(for list block only),
  // so need to handle the both case
  const blockType = currentBlock.getType();
  const isListBlockType = !!blockType.includes("list");
  const styles =
    typeof elementStyle === "string" ? elementStyle.split(";") : [];

  // For the list item, alignment style will be "justify-content:*" (except "justify" case)
  const listAlignIndex =
    isListBlockType && !styles.includes("text-align:justify")
      ? styles.findIndex(style => style.startsWith("justify-content"))
      : -1;

  // For other than list item, alignment style will be "text-align:*"
  const textAlignIndex = styles.findIndex(style =>
    style.startsWith("text-align"),
  );

  // For line height "line-height:*"
  const lineHeightIndex = styles.findIndex(style =>
    style.startsWith("line-height"),
  );

  // to get the current applied styles(color, font-size and font-family) from meta data
  // for the styling of list element bullets on new line empty editor state
  const metaDataStyles = getStylesFromMetaData(metaData, editorState);

  if (
    metaDataStyles.length > 0 ||
    textAlignIndex ||
    listAlignIndex ||
    lineHeightIndex
  ) {
    const { currentBlock: newBlock } = getCurrentlySelectedBlock(splitState);
    const newBlockStyles = newBlock.data.get("elementStyle");
    let newStyles =
      typeof newBlockStyles === "string" ? elementStyle.split(";") : [];

    // for the list alignment style
    if (listAlignIndex > -1) {
      newStyles.push(styles[listAlignIndex]);
    }

    // for other than list, alignment style
    if (textAlignIndex > -1) {
      newStyles.push(styles[textAlignIndex]);
    }

    // for other than list, alignment style
    if (lineHeightIndex > -1) {
      newStyles.push(styles[lineHeightIndex]);
    }

    // for current applied styles "font-size, font-family & color" case
    newStyles = [...newStyles];
    if (newBlock.getText() === "") {
      newStyles = [...newStyles, ...metaDataStyles];
    }

    const newBlockData = blockData.set("elementStyle", newStyles.join(";"));
    const updatedContentState = setBlockData(
      newBlockData,
      contentState,
      newBlock.getKey(),
    );

    const newState = EditorState.push(
      splitState,
      updatedContentState,
      "split-block",
    );

    return newState;
  }

  return splitState;
};

export default splitBlock;
